import {
  Button,
  ListContainer,
  ListContext,
  PAGE_DEFAULT,
  PAGE_SIZE_UNLIMITED,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  pn,
} from "@compass/core";
import { LkStudentParentDisplayField, LkStudentParentDisplayFieldId, Permission, PermissionAction, StudentParentListDto, StudentParentListFilter, StudentParentViewResource } from "generated/openApi";
import { useList, useRouter } from "hooks";
import { studentParentPageRoute } from "pages/students/student-parents/StudentParentPage/StudentParentPage";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";

export const Parents = (props: { initialLoad: boolean; studentId: number }) => {
  const ROOT_PERMISSION: Permission = Permission.StudentParent;
  const navigate = useNavigate();
  const { matchTemplatePath } = useRouter();

  const [listFilter, setListFilter] = useState<StudentParentListFilter>({
    page: PAGE_DEFAULT,
    pageSize: PAGE_SIZE_UNLIMITED,
    sortObjects: [{ property: pn<StudentParentListDto>("fullName"), isDesc: false }],
    parentFilter: { studentId: props.studentId },
  });
  const [listItems, setListItems] = useState<StudentParentListDto[]>([]);
  const [viewResource, setViewResource] = useState<StudentParentViewResource>({});
  const [displayFields, setDisplayFields] = useState<LkStudentParentDisplayField[]>([]);

  let relationDisplayField = displayFields.find((x) => x.id === LkStudentParentDisplayFieldId.Relation);

  const { tableContainerRef, loadList } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.studentParents.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
  });

  useEffect(() => {
    if (props.initialLoad) {
      loadList(listFilter);
      fetchViewResource();
    }
  }, [props.initialLoad]);

  useEffect(() => {
    if (displayFields) {
      relationDisplayField = displayFields.find((x) => x.id === LkStudentParentDisplayFieldId.Relation);
    }
  }, [displayFields]);

  const fetchViewResource = () => {
    ApiService.studentParents.getViewResource().then((response) => {
      setViewResource(response);
      setDisplayFields(response.displayFields || []);
    });
  };

  return (
    <>
      <ListContainer
        fullHeight
        headerNoPadding
        header={
          <Stack
            direction="row"
            sx={{
              background: "#0d57a1",
              color: "info.main",
              px: 2,
              py: 1.2,
              fontSize: "20px",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="inherit">Parents</Typography>

            <Stack direction="row" alignItems="center" spacing="sm">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  variant="text"
                  onClick={() =>
                    navigate({
                      pathname: studentParentPageRoute.getPath({ id: 0, crudMode: "edit", screenType: "dialog" }),
                      search: studentParentPageRoute.getOptionalParams?.({
                        studentId: props.studentId,
                      }),
                    })
                  }
                  sx={{ minWidth: "auto", color: "info.main" }}
                >
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
        tableContainer={
          <TableContainer ref={tableContainerRef}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Full Name</TableCell>
                  {relationDisplayField && (<TableCell>Relation</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {listItems.map((listItem) => (
                  <TableRow
                    key={listItem.id}
                    onClick={() =>
                      navigate({
                        pathname: studentParentPageRoute.getPath({
                          id: listItem.id,
                          crudMode: "view",
                          screenType: "dialog",
                        }),
                      })
                    }
                  >
                    <TableCell>{listItem.fullName}</TableCell>
                    {relationDisplayField && (<TableCell>{listItem.relation}</TableCell>)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        }
      />

      <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        {matchTemplatePath(studentParentPageRoute.templatePath) && <Outlet />}
      </ListContext.Provider>
    </>
  );
};
